<script lang="ts">
  import {
    Button,
    Column,
    Dropdown,
    FormGroup,
    Grid,
    InlineNotification,
    Loading,
    NotificationActionButton,
    Row,
  } from "carbon-components-svelte";
  import type APIClient from "src/ts/APIClient";
  import { Helper } from "../../../ts/Helper";
  import type { ICanvas, ICanvasOptions } from "src/ts/Interfaces";
  import {
    optionsData,
    productId,
    settingsElementId,
  } from "./../../../ts/Stores";
  import Templates from "../../common/Templates.svelte";
  import PreviewImage from "../../common/PreviewImage.svelte";
  import ProductLoader from "./ProductLoader.svelte";
  import type { Canvas, RubberStampResult } from "src/ts/Classes";
  import { NCFields } from "./../../../ts/NCFields";

  export let client: APIClient;
  export let canvas: ICanvas;

  let initSuccess: boolean = false;
  let success: boolean = false;
  let errorMessage: string = null;
  let imageSrc: string = null;
  let feedbackColour: string = null;
  let canvasHeight: number = 0;
  let canvasWidth: number = 0;

  let canvasOptions: ICanvasOptions;
  optionsData.subscribe((value) => {
    canvasOptions = value.canvasOptions;
  });

  let ncfields = NCFields.Instance();
  let getImageSrc = async (canvas: Canvas) => {
    feedbackColour = "orange";
    ncfields.setSettings(canvas);
    await client
      .fetchRubberStampPreview(canvas)
      .then((result) => {
        imageSrc = result.image;
        canvasWidth = result.width;
        canvasHeight = result.height;
        feedbackColour = "lime";
        initSuccess = true;
      })
      .then(async () => {
        await loadProduct(canvasWidth, canvasHeight, canvas.stampType);
        success = true;
      })
      .catch((error) => {
        feedbackColour = "red";
        errorMessage = error.message;
      });
  };

  type ErrorInfo = { kind; title; description; showRetryBtn };
  let errorInfo: ErrorInfo = {
    kind: "error",
    title: "Error",
    description: "An error occured. Please try again.",
    showRetryBtn: true,
  };

  let getProduct = async (
    width: number,
    height: number,
    stampType: string,
  ): Promise<RubberStampResult> => {
    return await client
      .SearchBestMatchRubberStamp(width, height, stampType)
      .then((product) => {
        if (product.success) {
          settingsElementId.set(product.settingsControlId);
          productId.set(product.productId.toString());
        } else {
          errorInfo = {
            kind: "info",
            title: product.errorTitle,
            description: product.errorDescription,
            showRetryBtn: false,
          };
          throw new Error(product.errorTitle);
        }
        return product;
      });
  };

  let productPromise: Promise<RubberStampResult> = null;
  const loadProduct = async (
    width: number,
    height: number,
    stampType: string,
  ) => {
    productPromise = getProduct(width, height, stampType);
  };

  let updatePreview = async () => await getImageSrc(canvas);

  let retry = async () =>
    await loadProduct(canvasWidth, canvasHeight, canvas.stampType);

  $: getImageSrc(canvas);
</script>

<div class="preview">
  <div class="pane">
    <FormGroup legendText="Preview of your stamp designs" class="step" noMargin>
      {#if !success}
        <InlineNotification kind="error" subtitle={errorMessage} />
      {/if}

      {#if !initSuccess}
        <InlineNotification
          kind="info"
          subtitle="Your stamp preview will show here."
        />
      {/if}

      <PreviewImage
        {canvasHeight}
        {canvasWidth}
        sizeInPercentage={canvas.sizeInPercentage}
        {imageSrc}
        {feedbackColour}
      />

      {#if !canvas.hideHideSizeInPercentage}
        <Dropdown
          titleText="Preview Size"
          items={Helper.ToIdTextPair(canvasOptions.sizePercentages)}
          bind:selectedId={canvas.sizeInPercentage}
        />
      {/if}

      <Templates />
    </FormGroup>
  </div>

  <div class="pane">
    {#await productPromise}
      <div class="placeholderTile">
        <Loading withOverlay={false} />
      </div>
    {:then product}
      <ProductLoader
        {product}
        stampCategory={canvas.stampCategory}
        stampType={canvas.stampType}
      />
    {:catch error}
      <InlineNotification title={error.message} kind={errorInfo.kind}>
        <svelte:fragment slot="actions">
          <NotificationActionButton
            on:click={retry}
            style={!errorInfo.showRetryBtn && "display:none"}
          >
            Retry
          </NotificationActionButton>
        </svelte:fragment>
        <p>
          {@html errorInfo.description}
        </p>
      </InlineNotification>
    {/await}
  </div>
</div>
